import { computed } from 'vue';

import { GlobalState } from '@/global';
import { findE911LocationGaps, hasZynoTalkNumber } from '@/modules/talk/helpers/e911';
import { DataCache } from '@/shared/data/cache';
import { hasPermission } from '@/shared/helpers/permissions.helpers';

/**
 * Whether any extension is assigned to an e911 location that can't complete a 911 call — because
 * the location has no carrier emergency address, or because no number holds it.
 *
 * Drives the red marker on Admin → ZynoTalk → Overview, where the gaps are listed with a fix action.
 * Sibling of `isTalkOnboardingActive`: same permission gate (an admin task pointing at an admin
 * screen), same reliance on a shared cache entry rather than its own request, so the nav indicator
 * and the screen can never disagree about what is outstanding.
 *
 * One list, because the service decides: each extension carries the verdict on the location it sits
 * at, so nothing here has to be cross-referenced against numbers or carrier registrations. Read
 * lazily — a nav indicator must not be the thing that provokes the request for a user who never
 * opens Talk; `useTalkE911Gaps` is what kicks the load, from surfaces that want it live.
 */
export const talkE911GapCount = computed<number>(() => {
    if (!GlobalState.tenant.value?.isAdmin || !hasPermission('talk')) return 0;

    const extensions = DataCache.getLazyRef('talkExtensions').value;
    const numbers = DataCache.getLazyRef('talkNumbers').value;
    // Absent is not "none": claiming a gap (or the absence of one) before the list lands would be a
    // guess that flickers when it arrives.
    if (!extensions || !numbers) return 0;
    // BYOC-only tenants place 911 through their own carrier, so there is nothing for us to flag.
    if (!hasZynoTalkNumber(numbers)) return 0;

    return findE911LocationGaps(extensions.filter(e => !e.deletedAt)).length;
});

/** Returns the shared count, kicking the (gated, deduped) load if it hasn't run yet. */
export function useTalkE911Gaps() {
    if (GlobalState.tenant.value?.isAdmin && hasPermission('talk')) {
        DataCache.getRef('talkExtensions');
        DataCache.getRef('talkNumbers');
    }
    return talkE911GapCount;
}
